import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import SENTRY from 'config/sentry.singleton';
import ELID from 'constants/elid.const';
import { useSalonBasicInfo } from 'providers/SalonBasicInfo.provider';
import { selectCognitoAttributeSub } from 'utils/cognito.util';
import fromAnyToString from 'utils/converters/fromAnyToString';
import isFn from 'utils/predicates/function/isFn.util';

const IntercomWatcher = ({ sub }) => {
  const userId = fromAnyToString(sub);
  const { salonId, salonName: name } = useSalonBasicInfo();

  const lastAttachment = useRef(null);

  useEffect(() => {
    if (!userId || !salonId || !name) return;
    if (!isFn(window?.analytics?.group)) return;

    const alreadyAttached = lastAttachment.current;

    // Check if already attached to same values
    if (
      alreadyAttached &&
      alreadyAttached.userId === userId &&
      alreadyAttached.salonId === salonId &&
      alreadyAttached.name === name
    ) {
      return; // No change, do nothing
    }

    // If re-attaching (different from last), log to your SENTRY
    if (alreadyAttached) {
      SENTRY.error({
        logger: 'IntercomWatcher',
        tags: { component: 'IntercomWatcher' },
        extra: {
          previousUserId: alreadyAttached.userId,
          previousSalonId: alreadyAttached.salonId,
          previousSalonName: alreadyAttached.name,
          currentUserId: userId,
          currentSalonId: salonId,
          currentSalonName: name,
          currentUrl: window.location.href,
          timestamp: new Date().toISOString(),
        },
        error: new Error(`IntercomWatcher: Re-attaching user`),
      });
    }

    // Now attach the new data
    window.analytics.group(salonId, { name }, { userId });

    // Save current attachment
    lastAttachment.current = { userId, salonId, name };
  }, [userId, salonId, name]);

  return <div data-bem="IntercomWatcher" id={ELID.intercomwatch} />;
};

const mapStateToProps = (state) => ({
  sub: selectCognitoAttributeSub(state),
});

const IntercomWatcherHoc = withRouter(connect(mapStateToProps)(IntercomWatcher));

export default IntercomWatcherHoc;
